/* System defined core library */
import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

/* User defined library/components */
import { DynamicComponentService } from '../../../../shared/services/dynamic.component.service';
import { SelectedExamDetailService } from '../../services/selected-exam-detail.service';

/* User defined models */
import { DynamicFormBase } from '../../../../shared/models/common/dynamic-form-base.model';
import { Dictionary } from '../../../../shared/models/common/shared.model';
import { AppointmentDetail } from '../../../../shared/models/scheduling/appointmentdetail.model';
import { Candidate } from '../../../../shared/models/scheduling/candidate.model';
import { Client } from '../../../../shared/models/scheduling/client.model';
import { Program } from '../../../../shared/models/scheduling/program.model';


@Component({
  selector: 'app-candidate',
  templateUrl: './candidate.component.html',
  providers: [DynamicComponentService]
})
export class CandidateComponent implements OnInit {

  /* User defined private/public variables  */
  public formStyle: string;
  public dynamicInputCtrls: DynamicFormBase<any>[];
  public selectedClient: Client;
  public selectedProgram: Program;
  public selectedAppointments: AppointmentDetail[] = [];
  public candidate: Candidate;

  /* Service subscriptions */
  client$: Subscription;
  program$: Subscription;
  candidate$: Subscription;
  lpiCandidate$: Subscription;
  selectedAppointments$: Subscription;
  translateLanguage$: Subscription;
  dynamicInputCtrls$: Subscription;

  /* Input-OutPut parameters decalartions */
  @Output() isDynamicFromValid = new EventEmitter<boolean>();
  @Output() isFormLoaded = new EventEmitter<boolean>();

  /* Constructor declaration with required injected services */
  constructor(
    private dynamicComponentService: DynamicComponentService,
    private selectedExamDetailService: SelectedExamDetailService,
    private translate: TranslateService
  ) {

    translate.setDefaultLang(this.selectedExamDetailService.defaultLanguageCode);
    this.translateLanguage$ = selectedExamDetailService.languageCode.subscribe(
      languageCode => {
        translate.use(languageCode);
      });

    this.client$ = selectedExamDetailService.client.subscribe(
      client => {
        this.selectedClient = client;
      });

    this.program$ = selectedExamDetailService.program.subscribe(
      program => {
        this.selectedProgram = program;
      });

    this.selectedAppointments$ = selectedExamDetailService.selectedAppointmentDetails.subscribe(
      selectedAppointments => {
        this.selectedAppointments = selectedAppointments;
      });

    this.candidate$ = selectedExamDetailService.candidate.subscribe(
      candidate => {
        this.candidate = candidate;
      });

    this.lpiCandidate$ = selectedExamDetailService.lpiCandidate.subscribe(
      lpiCandidate => {
        if (this.candidate == null) this.candidate = lpiCandidate;
      });

    this.dynamicInputCtrls$ = selectedExamDetailService.formInputData.subscribe(
      formInputData => {
        this.dynamicInputCtrls = formInputData;
      });
  }

  /* Initialize the component & call require methods when component loaded first time. */
  ngOnInit() {
    let selectedIndex = this.selectedExamDetailService.selectedAppointmentIndex;
    const exam = this.selectedAppointments[selectedIndex].exam;

    this.dynamicComponentService.getCandiateProfileMetaData(
      this.selectedClient.code,
      this.selectedProgram.code,
      exam.code
    ).subscribe((data: DynamicFormBase<string>[]) => {
        if (!this.dynamicInputCtrls) {
          this.dynamicInputCtrls = data;
        }
        // first time when creating demophics or prepopulating the eligibility demographics
        if (this.dynamicInputCtrls && (!this.candidate || (this.candidate && (this.candidate.defaultDetails)) || (this.candidate && !(this.candidate.details)))) {
          this.candidate = this.candidate || new Candidate();
          this.candidate.isAllRequiredValuesExist = true;
          this.candidate.details = new Dictionary();
          this.candidate.defaultInitialDetails = this.candidate.defaultDetails;
          this.candidate.demographicTemplateKeys = new Dictionary();

          this.dynamicInputCtrls.forEach((ctrlObj) => {
            this.candidate.demographicTemplateKeys.add(ctrlObj.key.toString().toLowerCase(), 'key');
            this.candidate.demographicTemplateKeys.add(ctrlObj.fieldMap.toString().toLowerCase(), 'fieldMap');

            if (this.candidate.defaultDetails && this.candidate.defaultDetails._keys.length) {
              if (this.candidate.defaultDetails.hasOwnProperty(ctrlObj.key.toString().toLowerCase())
                || this.candidate.defaultDetails.hasOwnProperty(ctrlObj.fieldMap.toString().toLowerCase())
              ) {
                ctrlObj.key = ctrlObj.key.toString().toUpperCase();
                // set if dataObject flag if all values on the form exist on eligibility
                if (ctrlObj.required && ctrlObj.displayFlag && !(this.candidate.defaultDetails[ctrlObj.key.toString().toLowerCase()] || this.candidate.defaultDetails[ctrlObj.fieldMap.toString().toLowerCase()])) {
                  this.candidate.isAllRequiredValuesExist = false;
                }

                if (ctrlObj.key.toString().toLowerCase() !== 'birth_date' && ctrlObj.controlType !== 'linkedCombo') {
                  ctrlObj.value = this.candidate.defaultDetails[ctrlObj.key.toString().toLowerCase()];
                } else if (ctrlObj.controlType === 'linkedCombo') {
                  ctrlObj.setValue(this.candidate.defaultDetails[ctrlObj.key.toString().toLowerCase()]);
                }

                // set candidate details from eligibility or lpi or cms demographics
                this.candidate.details.add(ctrlObj.key, this.candidate.defaultDetails[ctrlObj.key.toString().toLowerCase()]);
                if (!this.candidate.details.containsKey(ctrlObj.key)) {
                  this.candidate.details.add(ctrlObj.key, this.candidate.defaultDetails[ctrlObj.fieldMap.toString().toLowerCase()]);
                }

                if (ctrlObj.controlType === 'datepicker' && this.candidate.details[ctrlObj.key] && !this.candidate.details[ctrlObj.key]['jsdate']) {
                  if (this.candidate.details[ctrlObj.key]) {
                    const splitDate: any[] = this.candidate.details[ctrlObj.key] ? this.candidate.details[ctrlObj.key].toString().split(/[\/\-]/) : [];

                    // Value from eligibility comes in the format 'YYYY-MM-DD', but Lpi comes in the format 'MM/DD/YYYY'
                    let year, month, day;
                    if (splitDate[0].length === 4) {
                      [year, month, day] = splitDate;
                    } else if (splitDate[2].length === 4) {
                      [month, day, year] = splitDate;
                    }

                    const dateValue = { jsdate: new Date(year, month - 1, day) };

                    this.candidate.details[ctrlObj.key] = dateValue;
                    this.selectedExamDetailService.SetCandidateDataSource(this.candidate);

                    ctrlObj.value = dateValue;
                  }
                  //// filter out the non displayable fields from the form
                  const dynamicInputCtrls = this.dynamicInputCtrls.filter((ctrlObj: any) => {
                    return ctrlObj.displayFlag;
                  });
                  var requiredDems = dynamicInputCtrls.filter(dem => dem.required);
                  this.isDynamicFromValid.emit(requiredDems.every(dem => dem.value));
                }

              } else {
                // validate email field is not one of the candidate default details
                // so need to make sure the form value if prefilled when email is on eligibility
                if (ctrlObj.key.toString().toLowerCase() === 'validate_email' && this.candidate.defaultDetails.hasOwnProperty("email")) {
                  ctrlObj.value = this.candidate.defaultDetails["email"];
                }

                // Short term solution - Support non-standard demographics for first and last name
                if (ctrlObj.key.toString().toLowerCase() === 'firstname_rsa' && this.candidate.defaultDetails.hasOwnProperty('first_name')) {
                  ctrlObj.value = this.candidate.defaultDetails['first_name'];
                } else if (ctrlObj.key.toString().toLowerCase() === 'lastname_rsa' && this.candidate.defaultDetails.hasOwnProperty('last_name')) {
                  ctrlObj.value = this.candidate.defaultDetails['last_name'];
                }

                if (ctrlObj.required && ctrlObj.displayFlag && !ctrlObj.value) {
                  this.candidate.isAllRequiredValuesExist = false;
                }
                this.candidate.details.add(ctrlObj.key, null);
              }
            } else {

              if (ctrlObj.controlType === 'linkedCombo') {
                ctrlObj.setValue('');
              }

              this.candidate.details.add(ctrlObj.key, null);
              this.candidate.isAllRequiredValuesExist = false;
            }
            const dynamicInputCtrls = this.dynamicInputCtrls.filter((ctrlObj: any) => {
              return ctrlObj.displayFlag;
            });
            var requiredDems = dynamicInputCtrls.filter(dem => dem.required);
            this.isDynamicFromValid.emit(requiredDems.every(dem => dem.value));
          });

          if (this.candidate.details.hasOwnProperty("EMAIL") && this.candidate.details.hasOwnProperty("VALIDATE_EMAIL")) {
            this.candidate.details["VALIDATE_EMAIL"] = this.candidate.details["EMAIL"];
          }

          // Short term solution - Support non-standard demographics for first and last name
          if (!!this.candidate.defaultDetails) {
            if (this.candidate.details.hasOwnProperty('FIRSTNAME_RSA') && this.candidate.defaultDetails.hasOwnProperty('first_name')) {
              this.candidate.details['FIRSTNAME_RSA'] = this.candidate.defaultDetails['first_name'];
            }

            if (this.candidate.details.hasOwnProperty('LASTNAME_RSA') && this.candidate.defaultDetails.hasOwnProperty('last_name')) {
              this.candidate.details['LASTNAME_RSA'] = this.candidate.defaultDetails['last_name'];
            }
          }

          // filter out the non displayable fields from the form
          const dynamicInputCtrls = this.dynamicInputCtrls.filter((ctrlObj: any) => {
            return ctrlObj.displayFlag;
          });

          var requiredDems = dynamicInputCtrls.filter(dem => dem.required);
          this.isDynamicFromValid.emit(requiredDems.every(dem => dem.value));

          this.selectedExamDetailService.setFormInputData(this.dynamicInputCtrls);
          this.selectedExamDetailService.SetCandidateDataSource(this.candidate);

          this.formStyle = "vertical";
          this.isFormLoaded.emit(true);

        } else if (this.candidate && !this.candidate.defaultDetails && this.candidate.details) {

          this.dynamicInputCtrls.forEach((ctrlObj) => {
            if (ctrlObj.controlType === 'linkedCombo') {
              ctrlObj.setValue(this.candidate.details[ctrlObj.key]);
            } else {
              ctrlObj.value = this.candidate.details[ctrlObj.key];
            }
          });

          // filter out the non displayable fields from the form
          const dynamicInputCtrls = this.dynamicInputCtrls.filter((ctrlObj: any) => {
            return ctrlObj.displayFlag;
          });
          var requiredDems = dynamicInputCtrls.filter(dem => dem.required);
          this.isDynamicFromValid.emit(requiredDems.every(dem => dem.value));
          this.candidate.isAllRequiredValuesExist = true;
          this.selectedExamDetailService.setFormInputData(this.dynamicInputCtrls);
          this.selectedExamDetailService.SetCandidateDataSource(this.candidate);

          this.formStyle = "vertical";
          this.isFormLoaded.emit(true);
        }
      });
  }

  /* Emit flag whether dynamic form is valid or not. */
  setFormValidFlag(event: any): void {
    this.isDynamicFromValid.emit(event);
  }

  setFormLoadedFlag(event: any): void {
    this.isFormLoaded.emit(event);
  }
}
